import { z } from "zod";
import { pageSchema } from "@/helpers/types";

/** Wire shapes for `/datamates/{datamate_id}/rules`. */

export const RULE_STATUSES = ["pending", "active", "rejected", "retired", "superseded"] as const;
export const RuleStatusSchema = z.enum(RULE_STATUSES);
export type RuleStatus = z.infer<typeof RuleStatusSchema>;
export const RuleStatus = RuleStatusSchema.enum;

export const RuleChangeSchema = z.enum(["add", "edit", "retire"]);
export type RuleChange = z.infer<typeof RuleChangeSchema>;
export const RuleChange = RuleChangeSchema.enum;

/** The Rules tab's sub-views, each one list query. */
export const RuleViewSchema = z.enum(["pending", "active", "history"]);
export type RuleView = z.infer<typeof RuleViewSchema>;
export const RuleView = RuleViewSchema.enum;

/** What the owner decides on a pending proposal. */
export const RuleReviewActionSchema = z.enum(["approve", "approve_anyway", "reject"]);
export type RuleReviewAction = z.infer<typeof RuleReviewActionSchema>;
export const RuleReviewAction = RuleReviewActionSchema.enum;

/** What the owner does to an active rule. */
export const RuleOwnerActionSchema = z.enum(["pin", "unpin", "retire"]);
export type RuleOwnerAction = z.infer<typeof RuleOwnerActionSchema>;
export const RuleOwnerAction = RuleOwnerActionSchema.enum;

/** The `code` of a 409 the rules API answers a review, pin or retire with. */
export const RuleConflictCodeSchema = z.enum([
  "version_conflict",
  "invalid_status",
  "target_changed",
  "verification_flagged",
  "too_many_rules",
  "conflict",
]);
export type RuleConflictCode = z.infer<typeof RuleConflictCodeSchema>;
export const RuleConflictCode = RuleConflictCodeSchema.enum;

/** A step in a rule's trail. */
export const RuleEventActionSchema = z.enum([
  "corrected",
  "proposed",
  "approved",
  "rejected",
  "retired",
  "pinned",
  "unpinned",
  "superseded",
]);
export type RuleEventAction = z.infer<typeof RuleEventActionSchema>;
export const RuleEventAction = RuleEventActionSchema.enum;

export const RuleSettingsSchema = z.object({
  learning_enabled: z.boolean(),
  include_quotes: z.boolean(),
  share_across_repos: z.boolean(),
});
export type RuleSettingsValues = z.infer<typeof RuleSettingsSchema>;

export type RuleSettingsPayload = Partial<RuleSettingsValues>;

export const RulesSummarySchema = z.object({
  is_owner: z.boolean(),
  owner_name: z.string().nullable(),
  settings: RuleSettingsSchema,
  revision: z.number(),
  pending_count: z.number(),
  active_count: z.number(),
  history_count: z.number(),
});
export type RulesSummary = z.infer<typeof RulesSummarySchema>;

const RuleUsageSchema = z.object({
  applied: z.number(),
  helpful: z.number(),
  harmful: z.number(),
  corrected_again: z.number(),
});
export type RuleUsage = z.infer<typeof RuleUsageSchema>;

const RuleCorrectionSchema = z.object({
  excerpt: z.string(),
  corrected_at: z.string(),
  corrected_by_name: z.string().nullable(),
});
export type RuleCorrection = z.infer<typeof RuleCorrectionSchema>;

/** The active rule an edit or retire proposal changes, as it stands now. */
const ReplacedRuleSchema = z.object({
  public_id: z.string(),
  text: z.string(),
  applies_to: z.array(z.string()),
  version: z.number(),
  status: RuleStatusSchema,
});

export const RuleDetailSchema = z.object({
  public_id: z.string(),
  rule_key: z.string(),
  repo_identity: z.string().nullable(),
  text: z.string(),
  applies_to: z.array(z.string()),
  tags: z.array(z.string()),
  pinned: z.boolean(),
  status: RuleStatusSchema,
  change: RuleChangeSchema,
  // A string, not an enum: a new origin from the client must not fail the list.
  origin: z.string(),
  why: z.string().nullable(),
  replaces: ReplacedRuleSchema.nullable(),
  evidence_sessions: z.number(),
  corrections: z.array(RuleCorrectionSchema),
  weakens_verification: z.boolean(),
  verification_reason: z.string().nullable(),
  // The proposer, `why` and the corrections come only to the workspace owner
  // and the proposer; everyone else gets null / [].
  proposed_by: z.number().nullable(),
  proposed_by_name: z.string().nullable(),
  proposed_at: z.string(),
  reviewed_by_name: z.string().nullable(),
  reviewed_at: z.string().nullable(),
  review_reason: z.string().nullable(),
  usage: RuleUsageSchema,
  edit_pending: z.boolean(),
  version: z.number(),
  updated_at: z.string(),
});
export type RuleDetail = z.infer<typeof RuleDetailSchema>;

export const RuleListResponseSchema = pageSchema(RuleDetailSchema);
export type RuleListResponse = z.infer<typeof RuleListResponseSchema>;

export interface RuleListParams {
  view: RuleView;
  page: number;
  size: number;
}

/** The API rejects a longer reject or retire reason with a 422. */
export const MAX_REASON_LENGTH = 2000;

export interface RuleReviewPayload {
  action: RuleReviewAction;
  expected_version: number;
  reason?: string;
}

export interface RuleActionPayload {
  expected_version: number;
  reason?: string;
}

export const RuleEventSchema = z.object({
  id: z.number(),
  // Null for a former member, and for a correction or proposal the viewer may
  // not attribute (not the owner, not the proposer).
  // An action this client does not know yet still lists, as sent.
  action: RuleEventActionSchema.or(z.string()),
  actor_name: z.string().nullable(),
  // "cli" or "web"; a string so a new client does not fail the trail.
  actor_source: z.string(),
  reason: z.string().nullable(),
  rule_public_id: z.string(),
  created_at: z.string(),
});
export type RuleEvent = z.infer<typeof RuleEventSchema>;

export const RuleEventsResponseSchema = z.array(RuleEventSchema);
